import React, { useEffect, useMemo, useState } from 'react';
import RichTextEditor from './RichTextEditor';
import usePlaceholderCatalog from '../hooks/usePlaceholderCatalog';
import { validatePlaceholders } from '../utils/placeholderValidation';
import { getTemplates } from '../../../../services/emailTemplateService';

/**
 * Center composer panel: subject, rich body editor, template picker, custom-variable
 * fill-in for the template's contextual placeholders, and live unknown-placeholder validation.
 */
const ComposerPanel = ({ subject, body, customVariables, onSubjectChange, onBodyChange, onTemplateApply, onCustomVariablesChange }) => {
    const { tokens: systemTokens } = usePlaceholderCatalog();
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');

    useEffect(() => {
        getTemplates().then((data) => setTemplates(data?.content || data || [])).catch(() => setTemplates([]));
    }, []);

    const combinedText = `${subject || ''} ${body || ''}`;
    const customTokenNames = Object.keys(customVariables || {});
    const validation = useMemo(
        () => validatePlaceholders(combinedText, systemTokens, customTokenNames),
        [combinedText, systemTokens, customTokenNames]
    );

    const applyTemplate = (id) => {
        setSelectedTemplateId(id);
        const tpl = templates.find((t) => String(t.id) === String(id));
        if (tpl) onTemplateApply?.(tpl);
    };

    // Custom placeholders are any unknown token the admin wants to resolve manually per-send.
    const unresolvedCustom = validation.unknown;

    return (
        <div className="admin-card mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0"><i className="bi bi-envelope-paper me-2"></i>Compose</h5>
                <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={selectedTemplateId}
                    onChange={(e) => applyTemplate(e.target.value)}
                >
                    <option value="">Start from template…</option>
                    {templates.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
            </div>

            <div className="mb-3">
                <label className="form-label fw-semibold">Subject</label>
                <input
                    type="text"
                    className="form-control"
                    value={subject}
                    onChange={(e) => onSubjectChange(e.target.value)}
                    placeholder="Email subject — supports {{placeholders}}"
                />
            </div>

            <div className="mb-3">
                <label className="form-label fw-semibold">Body</label>
                <RichTextEditor value={body} onChange={onBodyChange} placeholder="Write your email... use {{firstName}} etc. for placeholders" />
            </div>

            {unresolvedCustom.length > 0 && (
                <div className="border-top pt-3">
                    <div className="fw-semibold mb-2 text-warning">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Unknown placeholders detected — fill in a value or fix the token:
                    </div>
                    {unresolvedCustom.map((token) => (
                        <div className="mb-2" key={token}>
                            <label className="form-label small">{`{{${token}}}`}</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={customVariables?.[token] || ''}
                                onChange={(e) => onCustomVariablesChange?.({ ...customVariables, [token]: e.target.value })}
                                placeholder={`Value to substitute for {{${token}}}`}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ComposerPanel;
